'use client'
import React from 'react'
import { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import {
  Typography,
  Button,
  Row,
  Col,
  message,
  Spin,
  Card,
  Tooltip,
} from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  ReloadOutlined,
  LockOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons'
import ScoreDrawer from './score-drawer'
import styled from 'styled-components'

import { assignPracticeAssessment } from 'utils/assignPracticeAssessment'
import {
  fetchCommunicationPracticeAssessments,
  fetchReports,
  generatePDFReport,
  checkRetakeEligibility,
  fetchPracticeScores,
} from '../../actions'
import selectors, { selectCommunicationDayZero } from '../../selectors'
import { trackPracticeSessionStarted } from '../../../../utils/assessmentEvents'

const { Title, Text } = Typography

// Demo chart data for preview - Enhanced with more realistic Communication progression
const demoChartData = [
  { session: 0, Reading: 0, Listening: 0, Speaking: 0, 'Combined Progress': 0 },
  {
    session: 1,
    Reading: 25,
    Listening: 20,
    Speaking: 15,
    'Combined Progress': 20,
  },
  {
    session: 2,
    Reading: 42,
    Listening: 35,
    Speaking: 28,
    'Combined Progress': 35,
  },
  {
    session: 3,
    Reading: 58,
    Listening: 48,
    Speaking: 42,
    'Combined Progress': 49,
  },
  {
    session: 4,
    Reading: 71,
    Listening: 62,
    Speaking: 55,
    'Combined Progress': 63,
  },
  {
    session: 5,
    Reading: 82,
    Listening: 74,
    Speaking: 68,
    'Combined Progress': 75,
  },
  {
    session: 6,
    Reading: 90,
    Listening: 83,
    Speaking: 79,
    'Combined Progress': 84,
  },
  {
    session: 7,
    Reading: 95,
    Listening: 89,
    Speaking: 86,
    'Combined Progress': 90,
  },
]

// Central CTA Card Components
const CentralCTAOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  pointer-events: none;
`

const CentralCTACard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8e8e8;
  text-align: center;
  max-width: 480px;
  width: 100%;
  pointer-events: all;
  backdrop-filter: none;

  @media (max-width: 768px) {
    padding: 32px 24px;
    max-width: 400px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
    max-width: 320px;
    margin: 0 16px;
  }
`

const CTAIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.3);

  .anticon {
    font-size: 28px;
    color: #ffffff;
  }

  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    margin-bottom: 20px;

    .anticon {
      font-size: 24px;
    }
  }
`

const CTATitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #262626;
  margin: 0 0 16px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`

const CTADescription = styled.p`
  font-size: 16px;
  color: #595959;
  margin: 0 0 32px 0;
  line-height: 1.6;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 28px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 24px;
    line-height: 1.5;
  }
`

const CTAButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
  }

  &:focus {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  }

  @media (max-width: 480px) {
    height: 44px;
    padding: 0 24px;
    font-size: 15px;
  }
`

// Blur Wrapper Component
const BlurWrapper = styled.div`
  filter: blur(5px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  transition: all 0.3s ease;
`

// Professional styled components
const StyledCommunicationContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  position: relative;
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background-color: #ffffff;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 80px;
`

const PageTitle = styled(Title)`
  color: #262626 !important;
  margin-bottom: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`

const ContinueButton = styled(Button)`
  background-color: #1890ff;
  border-color: #1890ff;
  color: #ffffff;
  font-weight: 500;
  height: 40px;
  padding: 0 24px;
  border-radius: 6px;
  &:hover {
    background-color: #40a9ff;
    color: #ffffff;
    border-color: #40a9ff;
  }
  &:focus {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
  &:disabled {
    background-color: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
  }
`

// Section with overlay loader support
const SectionWithOverlay = styled.div`
  position: relative;
  margin-bottom: 24px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: ${props => props.minHeight || 'auto'};

  &.has-overlay {
    .section-content {
      filter: blur(2px);
      opacity: 0.6;
      pointer-events: none;
    }
  }
`

const SectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  gap: 16px;

  .overlay-spinner {
    .ant-spin-dot {
      font-size: 24px;
    }
  }

  .overlay-text {
    color: #595959;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
  }
`

const VisualizationSection = styled(SectionWithOverlay)`
  min-height: 500px;
`

const PracticeSection = styled(SectionWithOverlay)`
  min-height: 100px;
`

const HistorySection = styled(SectionWithOverlay)`
  min-height: 300px;
`

const ChartContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
  height: 400px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  padding: 16px;
  overflow: hidden;

  &.demo-mode {
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      backdrop-filter: blur(2px);
      background: rgba(255, 255, 255, 0.6);
      z-index: 1;
    }

    &::after {
      display: none; // Hide the default message
    }

    .baseline-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 255, 255, 0.95);
      padding: 24px 32px;
      border-radius: 8px;
      text-align: center;
      border: 1px solid #e8e8e8;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      z-index: 2;
      min-width: 300px;

      .title {
        font-size: 18px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 12px;
      }

      .description {
        font-size: 14px;
        color: #8c8c8c;
        margin-bottom: 16px;
      }

      .scores {
        color: #1890ff;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 16px;
        text-align: left;

        .score-item {
          display: flex;
          justify-content: space-between;
          gap: 24px;

          &.total {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #d9d9d9;
            font-weight: 600;
          }
        }
      }
    }
  }
`

const ProgressSection = styled.div`
  background-color: #fafafa;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  border: 1px solid #f0f0f0;
`

const ProgressTitle = styled(Title)`
  color: #262626 !important;
  font-size: 16px !important;
  margin-bottom: 16px !important;
  font-weight: 600 !important;
`

const ProgressValue = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: #52c41a;
  margin-left: 8px;
`

const HistoryHeader = styled(Title)`
  color: #262626 !important;
  font-size: 18px !important;
  margin-bottom: 8px;
  font-weight: 600;
`

const SubHeaderText = styled(Text)`
  color: #8c8c8c;
  font-size: 14px;
  display: block;
  margin-bottom: 20px;
`

const OldPracticeItem = styled.div`
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 16px;
  padding: 20px 24px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  &:hover {
    border-color: rgb(134, 134, 134);
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.1);
  }
  &:last-child {
    margin-bottom: 0;
  }
`

const OldPracticeContent = styled(Row)`
  align-items: center;
  min-height: 60px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PracticeItemLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`

const DateBadge = styled.span`
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const LatestBadge = styled.span`
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  white-space: nowrap;
`

const OldPracticeTitle = styled.div`
  color: rgb(59, 59, 59);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  @media (max-width: 768px) {
    font-size: 14px;
    margin-top: 4px;
  }
`

const ScoresContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  @media (max-width: 1200px) {
    gap: 12px;
  }
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }
  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const ScoreItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  .label {
    font-size: 11px;
    color: #8c8c8c;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .value {
    font-size: 13px;
    color: #262626;
    font-weight: 600;
    line-height: 1.2;
  }
  @media (max-width: 768px) {
    min-width: 70px;
    .label {
      font-size: 10px;
    }
    .value {
      font-size: 12px;
    }
  }
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }
`

const ViewScoreButton = styled(Button)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #d9d9d9;
  color: #595959;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 16px;
  height: 32px;
  border-radius: 4px;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    border-color: #1890ff;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }
  &:focus:not(:disabled) {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    border-color: #1890ff;
    color: #ffffff;
  }
  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
  }
`

const RetakeButton = styled(Button)`
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  border: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 16px;
  height: 32px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.2);
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #73d13d 0%, #95de64 100%);
    box-shadow: 0 4px 12px rgba(82, 196, 26, 0.4);
    color: #ffffff;
  }
  &:focus:not(:disabled) {
    background: linear-gradient(135deg, #73d13d 0%, #95de64 100%);
    color: #ffffff;
  }
  &:disabled {
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
    box-shadow: none;
  }
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 12px;
    height: 30px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #8c8c8c;
  min-height: 200px;
  .loading-text {
    margin-top: 12px;
    font-size: 14px;
    color: #595959;
  }
  .ant-spin {
    margin-bottom: 8px;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  min-height: 200px;
  .error-title {
    font-size: 16px;
    font-weight: 600;
    color: #cf1322;
    margin-bottom: 8px;
  }
  .error-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 16px;
    max-width: 400px;
    line-height: 1.4;
  }
`

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  min-height: 200px;
  .empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 8px;
  }
  .empty-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 20px;
    max-width: 400px;
    line-height: 1.4;
  }
`

const ViewMoreButton = styled(Button)`
  width: 100%;
  max-width: 300px;
  margin: 20px auto 0 auto;
  height: 40px;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #595959;
  font-weight: 500;
  border-radius: 4px;
  display: block;
  &:hover:not(:disabled) {
    color: #1890ff;
    border-color: #1890ff;
  }
  &:focus:not(:disabled) {
    color: #1890ff;
    border-color: #1890ff;
  }
  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
  }
`

const SummaryCard = styled.div`
  background: ${props => props.bgColor || '#fafafa'};
  border-radius: 6px;
  padding: 16px;
  text-align: center;
  border: 1px solid ${props => props.borderColor || '#f0f0f0'};
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`

const CardTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #8c8c8c;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CardValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.color || '#595959'};
  margin-bottom: 4px;
`

const PerformanceIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: ${props => props.bgColor || '#f6ffed'};
  border-radius: 6px;
  border: 1px solid ${props => props.borderColor || '#d9f7be'};
`

const TrendIcon = styled.span`
  font-size: 16px;
`

const TrendText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.color || '#52c41a'};
`

const Communication = ({ auth, onAssessmentAssigned, assignedAssessment }) => {
  // State management
  const [loading, setLoading] = useState(false)
  const [retakeLoading, setRetakeLoading] = useState(false)
  const [scoreDrawerVisible, setScoreDrawerVisible] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [reportsLoading, setReportsLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // Refs to prevent multiple API calls
  const practiceHistoryFetched = useRef(false)
  const reportsFetched = useRef(false)
  const retakeEligibilityFetched = useRef(false)
  const practiceScoresFetched = useRef(false)

  const dispatch = useDispatch()
  const user = useSelector(state => state.auth.user)
  const authStudentId = useSelector(state => state.auth?.studentId)
  const studentDetails = useSelector(state => state.studentData?.studentDetails)
  const authStudentEmail = studentDetails?.admin?.email

  // Redux selectors - Don't filter as API doesn't include type field
  const allPracticeScores = useSelector(
    state => state.assessment?.practiceScores?.data || []
  )
  const practiceScores = allPracticeScores // Use all practice scores since they're all communication scores

  // Add practice access selectors
  const practiceAccessState = useSelector(
    state => state.assessment?.practiceAccess
  )
  const communicationAccess = practiceAccessState?.Communication || {
    hasAccess: false,
    completedCount: 0,
    requiredCount: 2,
  }
  const aptitudeAccess = practiceAccessState?.Aptitude || {
    hasAccess: false,
    completedCount: 0,
    requiredCount: 2,
  }

  // Check if user has any practice access
  const hasAnyPracticeAccess =
    communicationAccess.hasAccess || aptitudeAccess.hasAccess
  const hasNoPracticeAccess =
    !communicationAccess.hasAccess && !aptitudeAccess.hasAccess

  // Missing practice scores loading selector
  const practiceScoresLoading = useSelector(
    state => state.assessment?.practiceScores?.loading || false
  )
  const practiceScoresError = useSelector(
    state => state.assessment?.practiceScores?.error || null
  )
  const communicationDayZero = useSelector(selectCommunicationDayZero)

  // Use the type-specific selector for Communication assessments
  const practiceHistory = useSelector(selectors.getCommunicationPracticeHistory)
  const historyLoading = useSelector(selectors.getPracticeHistoryLoading)
  const historyError = useSelector(selectors.getPracticeHistoryError)
  const practiceHistoryPagination = useSelector(
    selectors.getPracticeHistoryPagination
  )

  // Add missing retake eligibility selectors
  const reports = useSelector(selectors.getReports)
  const reportsError = useSelector(selectors.getReportsError)
  const retakeEligibility = useSelector(selectors.getRetakeEligibility)
  const retakeEligibilityLoading = useSelector(
    selectors.getRetakeEligibilityLoading
  )
  const retakeEligibilityError = useSelector(
    selectors.getRetakeEligibilityError
  )

  // Enhanced error handling functions
  const showErrorMessage = (error, context = '') => {
    const errorMessage =
      error?.message || error?.toString() || 'An unexpected error occurred'
    message.error(`${context ? context + ': ' : ''}${errorMessage}`)
    console.error(`Error ${context}:`, error)
  }

  const showSuccessMessage = text => {
    message.success(text)
  }

  const showLoadingMessage = text => {
    return message.loading(text, 0)
  }

  // Assessment utility functions
  const getMostRecentAssessment = () => {
    if (practiceHistory && practiceHistory.length > 0) {
      return practiceHistory[0]
    }
    return null
  }

  const checkRetakeEligibilityForAssessment = async assessmentAssignedId => {
    try {
      console.log(
        '🔄 Checking retake eligibility for communication assessment:',
        assessmentAssignedId
      )
      const eligibilityData = await dispatch(
        checkRetakeEligibility(assessmentAssignedId)
      )
      console.log(
        '✅ Communication retake eligibility check completed:',
        eligibilityData
      )
      return eligibilityData
    } catch (error) {
      console.error(
        '❌ Failed to check communication retake eligibility:',
        error
      )
      throw error
    }
  }

  const isRetakeAvailable = assessment => {
    if (!assessment || !assessment.scoresCalculated) {
      return false
    }
    const eligibilityData = retakeEligibility
    if (
      eligibilityData &&
      eligibilityData.assessmentAssignedId === assessment.assessment_assigned_id
    ) {
      return eligibilityData.canRetake
    }
    return true // Default to available if no eligibility data yet
  }

  // Chart data preparation - Modified to handle demo data when no real data
  const generateChartData = () => {
    console.log(
      '🔍 generateChartData called with practiceScores:',
      practiceScores
    )
    console.log('🔍 Communication dayZero data:', communicationDayZero)

    // If no real data, return demo data for preview
    if (!practiceScores || practiceScores.length === 0) {
      // If we have dayZero data but no practice scores, create a chart with just dayZero
      if (communicationDayZero) {
        return [
          {
            session: 0,
            reading_score: communicationDayZero.reading || 0,
            listening_score: communicationDayZero.listening || 0,
            speaking_score: communicationDayZero.speaking || 0,
            writing_score: communicationDayZero.writing || 0,
            current_session_score: communicationDayZero.total_score || 0,
            combined_progress: communicationDayZero.total_score || 0,
            submittedAt: null,
            assessmentAssignedId: null,
            raw_reading: communicationDayZero.reading || 0,
            raw_listening: communicationDayZero.listening || 0,
            raw_speaking: communicationDayZero.speaking || 0,
            delta: 0,
            isOnlyDayZero: true,
          },
        ]
      }

      console.log(
        '❌ No practice scores available, returning demo data for preview'
      )
      return [] // Return empty array 
    }

    const sessions = []

    // Sort practice scores by submission date to ensure chronological order
    const sortedScores = [...practiceScores].sort(
      (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt)
    )

    console.log('🔍 Sorted practice scores:', sortedScores)

    // Add baseline session using dayZero data if available
    if (communicationDayZero) {
      sessions.push({
        session: 0,
        reading_score: communicationDayZero.reading || 0,
        listening_score: communicationDayZero.listening || 0,
        speaking_score: communicationDayZero.speaking || 0,
        writing_score: communicationDayZero.writing || 0,
        current_session_score: communicationDayZero.total_score || 0,
        combined_progress: communicationDayZero.total_score || 0,
        submittedAt: null,
        assessmentAssignedId: null,
        raw_reading: communicationDayZero.reading || 0,
        raw_listening: communicationDayZero.listening || 0,
        raw_speaking: communicationDayZero.speaking || 0,
        raw_writing: communicationDayZero.writing || 0,
        delta: 0,
      })
    } else {
      // Add baseline session with zeros if no dayZero data
      sessions.push({
        session: 0,
        reading_score: 0,
        listening_score: 0,
        speaking_score: 0,
        writing_score: 0,
        current_session_score: 0,
        combined_progress: 0,
        submittedAt: null,
        assessmentAssignedId: null,
        raw_reading: 0,
        raw_listening: 0,
        raw_speaking: 0,
        raw_writing: 0,
        delta: 0,
      })
    }

    sortedScores.forEach((scoreData, index) => {
      const scores = {
        reading: scoreData.reading || 0,
        listening: scoreData.listening || 0,
        speaking: scoreData.speaking || 0,
        writing: scoreData.writing || 0,
        total: scoreData.total_score || 0,
        delta: scoreData.delta || 0,
      }

      console.log(`🔍 Processing score ${index + 1}:`, scores)

      // Use direct scores from backend (no EMA calculation)
      sessions.push({
        session: index + 1, // This will now be 1, 2, 3, etc. after the initial 0
        reading_score: scores.reading, // Use direct score
        listening_score: scores.listening, // Use direct score
        speaking_score: scores.speaking, // Use direct score
        writing_score: scores.writing, // Use direct score
        current_session_score: scores.total,
        combined_progress: scores.total, // Use direct total score
        submittedAt: scoreData.submittedAt,
        assessmentAssignedId: scoreData.assessmentAssignedId,
        raw_reading: scores.reading,
        raw_listening: scores.listening,
        raw_speaking: scores.speaking,
        raw_writing: scores.writing,
        delta: scores.delta,
      })
    })

    console.log(
      '✅ Generated communication chart data from practice scores:',
      sessions
    )
    return sessions
  }

  const hasOnlyDayZeroData = () => {
    const chartData = generateChartData()
    // Check if we only have one data point (day 0) and no practice scores
    return (
      chartData.length === 1 &&
      (!practiceScores || practiceScores.length === 0) &&
      communicationDayZero
    )
  }

  const shouldShowDemoChart = () => {
    // Show demo chart when no real data at all
    return (
      (!practiceScores || practiceScores.length === 0) && !communicationDayZero
    )
  }

  const shouldBlurChart = () => {
    // Blur chart when we only have day 0 data or showing demo data
    return hasOnlyDayZeroData() || shouldShowDemoChart()
  }

  const prepareChartData = () => {
    const chartData = generateChartData()
    console.log('🔍 prepareChartData output:', chartData)
    const preparedData = chartData.map(session => ({
      session: session.session,
      Reading: session.reading_score,
      Listening: session.listening_score,
      Speaking: session.speaking_score,
      Writing: session.writing_score,
      'Combined Progress': session.combined_progress,
      delta: session.delta,
    }))
    console.log('🔍 Final prepared chart data:', preparedData)
    return preparedData
  }

  // Check if components should show empty states - Modified for demo data
  const hasChartData = generateChartData().length > 0
  const hasRealData =
    (practiceScores && practiceScores.length > 0) || communicationDayZero
  const hasHistoryData = practiceHistory && practiceHistory.length > 0

  // Event handlers
  const handleContinue = async () => {
    if (!authStudentEmail) {
      showErrorMessage(
        null,
        'Student email not found. Please refresh the page.'
      )
      return
    }

    setLoading(true)
    const loadingMessage = showLoadingMessage(
      'Assigning communication practice assessment...'
    )

    try {
      const response = await assignPracticeAssessment({
        studentEmail: authStudentEmail,
        assessmentType: 'Communication',
        cefrLevel: '',
      })

      if (response && response.data) {
        const newAssessment = {
          assessment_assigned_id: response.data.assessmentAssignedId,
          assessment_set_id: response.data.assessmentSetId,
          assessment_corporate_map_id: response.data.assessmentMapId,
          title: 'Communication Practice Assessment',
          type: 'Communication',
          startTime: response.data.startTime,
          endTime: response.data.endTime,
          duration: response.data.duration,
          isPractice: true,
          entityType: 'practice',
        }

        trackPracticeSessionStarted(newAssessment.assessment_assigned_id, null, "Communication", "Manual", false, newAssessment.entityType)
        onAssessmentAssigned(newAssessment)
        showSuccessMessage(
          'Communication practice assessment assigned successfully!'
        )
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      showErrorMessage(
        error,
        'Failed to assign communication practice assessment'
      )
    } finally {
      message.destroy()
      setLoading(false)
    }
  }

  const handleRetakeAssessment = async assessment => {
    if (!assessment) {
      showErrorMessage(null, 'Assessment not found')
      return
    }

    console.log('🔄 === COMMUNICATION RETAKE ASSESSMENT HANDLER START ===')
    console.log(
      '🔄 Communication retake button clicked for assessment:',
      assessment.id
    )

    const assessmentId = assessment.assessment_assigned_id || assessment.id
    setRetakeLoading(true)
    const loadingMessage = showLoadingMessage(
      'Starting communication retake assessment...'
    )

    try {
      // Check eligibility first
      console.log('🔄 Step 1: Checking communication retake eligibility...')
      const eligibilityData = await checkRetakeEligibilityForAssessment(
        assessmentId
      )

      if (!eligibilityData.canRetake) {
        console.error(
          '❌ Communication retake not eligible:',
          eligibilityData.reason
        )
        throw new Error(`Retake not available: ${eligibilityData.reason}`)
      }

      console.log(
        '✅ Step 1 SUCCESS: Communication retake eligibility confirmed'
      )
      // Create retake assessment object
      console.log(
        '🔄 Step 2: Creating communication retake assessment object...'
      )
      const retakeAssessment = {
        assessment_assigned_id: assessmentId,
        assessment_set_id: assessment.assessment_set_id,
        assessment_corporate_map_id: assessment.assessment_corporate_mapId,
        title: 'Communication Practice Assessment (Retake)',
        type: 'Communication',
        startTime: assessment.startTime,
        endTime: assessment.endTime,
        duration: assessment.duration,
        isPractice: true,
        entityType: 'practice',
        isRetake: true,
        originalAssessmentId: assessment.id,
      }

      console.log(
        '✅ Step 2 SUCCESS: Communication retake assessment object created'
      )
      console.log(
        '🔄 Communication retake assessment object:',
        retakeAssessment
      )

      // Call parent component
      console.log('🔄 Step 3: Calling parent component...')
      onAssessmentAssigned(retakeAssessment)
      console.log('✅ Step 3 SUCCESS: Parent component called')

      showSuccessMessage(
        'Starting retake of your most recent communication assessment!'
      )

      console.log(
        '🎉 === COMMUNICATION RETAKE ASSESSMENT HANDLER COMPLETED SUCCESSFULLY ==='
      )
    } catch (error) {
      console.error('❌ === COMMUNICATION RETAKE ASSESSMENT HANDLER ERROR ===')
      console.error('❌ Error details:', error)
      showErrorMessage(error, 'Failed to start communication retake assessment')
    } finally {
      message.destroy()
      setRetakeLoading(false)
      console.log('🔄 === COMMUNICATION RETAKE ASSESSMENT HANDLER END ===')
    }
  }

  const handleViewScore = async assessment => {
    // Ensure assessment has proper type information for communication
    const assessmentWithType = {
      ...assessment,
      type: 'Communication', // Explicitly set the type for communication assessments
    }
    setSelectedAssessment(assessmentWithType)
    setScoreDrawerVisible(true)

    const assessmentAssignedId =
      assessment.assessment_assigned_id || assessment.id
    if (!assessmentAssignedId) {
      showErrorMessage(null, 'Assessment ID not found')
      return
    }

    const existingReport = reports?.find(
      report => report.id === assessmentAssignedId
    )
    if (!existingReport && !reportsLoading) {
      try {
        setReportsLoading(true)
        await dispatch(fetchReports([assessmentAssignedId]))
      } catch (error) {
        showErrorMessage(error, 'Failed to load assessment reports')
      } finally {
        setReportsLoading(false)
      }
    }
  }

  const handleCloseScoreDrawer = () => {
    setScoreDrawerVisible(false)
    setSelectedAssessment(null)
  }

  // ponytail: dead prop - score-drawer does not render a download button
  const handleDownloadReport = assessmentId => {
    return dispatch(generatePDFReport(assessmentId))
  }

  const handleViewDetailedReport = assessmentId => {
    message.info('Detailed report functionality will be implemented soon.')
  }

  const handleLoadMore = async () => {
    if (
      !authStudentId ||
      !practiceHistoryPagination?.hasNextPage ||
      loadingMore
    ) {
      return
    }

    setLoadingMore(true)
    try {
      const nextPage = practiceHistoryPagination.nextPage
      console.log(
        `🔍 Loading more communication practice assessments - Page ${nextPage}`
      )
      // Use type-specific fetch function
      await dispatch(
        fetchCommunicationPracticeAssessments(authStudentId, nextPage, 5)
      )
      console.log(`✅ Successfully loaded communication page ${nextPage}`)
    } catch (error) {
      console.error(
        '❌ Failed to load more communication practice assessments:',
        error
      )
      showErrorMessage(error, 'Failed to load more assessments')
    } finally {
      setLoadingMore(false)
    }
  }

  const handleRetryFetch = () => {
    practiceHistoryFetched.current = false
    reportsFetched.current = false
    practiceScoresFetched.current = false
    retakeEligibilityFetched.current = false

    if (authStudentId) {
      dispatch(fetchCommunicationPracticeAssessments(authStudentId, 1, 5))
    }
    if (authStudentEmail) {
      dispatch(fetchPracticeScores(authStudentEmail))
    }
  }

  // Effects
  useEffect(() => {
    if (authStudentEmail && !practiceScoresFetched.current) {
      practiceScoresFetched.current = true
      dispatch(fetchPracticeScores(authStudentEmail)).catch(error => {
        console.error('Failed to fetch communication practice scores:', error)
        showErrorMessage(error, 'Failed to load practice scores')
        practiceScoresFetched.current = false
      })
    }
  }, [authStudentEmail, dispatch])

  useEffect(() => {
    if (authStudentId && !practiceHistoryFetched.current) {
      practiceHistoryFetched.current = true
      console.log(
        '🔄 Fetching communication practice assessments for student:',
        authStudentId
      )
      // Use type-specific fetch function
      dispatch(
        fetchCommunicationPracticeAssessments(authStudentId, 1, 5)
      ).catch(error => {
        console.error('Failed to fetch communication practice history:', error)
        showErrorMessage(error, 'Failed to load practice history')
        practiceHistoryFetched.current = false
      })
    }
  }, [authStudentId, dispatch])

  useEffect(() => {
    if (
      practiceHistory &&
      practiceHistory.length > 0 &&
      !reportsFetched.current
    ) {
      const assessmentIds = practiceHistory
        .map(assessment => assessment.assessment_assigned_id || assessment.id)
        .filter(id => id)

      if (assessmentIds.length > 0) {
        reportsFetched.current = true
        setReportsLoading(true)
        dispatch(fetchReports(assessmentIds))
          .catch(error => {
            console.error('Error fetching communication reports:', error)
            showErrorMessage(error, 'Failed to load reports')
            reportsFetched.current = false
          })
          .finally(() => {
            setReportsLoading(false)
          })
      }
    }
  }, [practiceHistory, dispatch])

  // Check retake eligibility for the most recent assessment
  useEffect(() => {
    const recentAssessment = getMostRecentAssessment()
    if (
      recentAssessment &&
      recentAssessment.scoresCalculated &&
      !retakeEligibilityFetched.current
    ) {
      const assessmentAssignedId = recentAssessment.assessment_assigned_id
      console.log(
        '🔄 Checking retake eligibility for most recent communication assessment:',
        assessmentAssignedId
      )
      retakeEligibilityFetched.current = true
      dispatch(checkRetakeEligibility(assessmentAssignedId))
        .then(eligibilityData => {
          console.log(
            '✅ Communication retake eligibility check completed:',
            eligibilityData
          )
        })
        .catch(error => {
          console.error(
            '❌ Failed to check communication retake eligibility:',
            error
          )
          showErrorMessage(error, 'Failed to check retake eligibility')
          retakeEligibilityFetched.current = false // Reset on error
        })
    }
  }, [practiceHistory, dispatch])

  // Reset retake eligibility check when practice history changes
  useEffect(() => {
    retakeEligibilityFetched.current = false
  }, [practiceHistory?.length])

  // Custom tooltip component for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const deltaValue = payload[0]?.payload?.delta || 0
      return (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <span
            style={{
              fontWeight: '600',
              margin: '0 0 8px 0',
              color: '#262626',
              fontSize: '14px',
            }}
          >
            {hasRealData ? `Day ${label}` : `Session ${label}`}
            {deltaValue !== 0 && (
              <span
                style={{
                  marginLeft: '8px',
                  color: deltaValue > 0 ? '#52c41a' : '#ff4d4f',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: '0.8',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {deltaValue > 0 ? '+' : ''}
                {deltaValue.toFixed(1)}%
                {deltaValue > 0 ? (
                  <RiseOutlined style={{ fontSize: '16px' }} />
                ) : (
                  <FallOutlined style={{ fontSize: '16px' }} />
                )}
              </span>
            )}
          </span>

          {payload.map((entry, index) => {
            const icons = {
              Reading: '',
              Listening: '',
              Speaking: '',
              'Combined Progress': '',
            }
            return (
              <p
                key={index}
                style={{
                  margin: '4px 0',
                  color: entry.color,
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                <span style={{ marginRight: '8px' }}>
                  {icons[entry.dataKey] || ''}
                </span>
                {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
              </p>
            )
          })}
        </div>
      )
    }
    return null
  }

  // Utility functions
  const formatAssessmentDate = dateString => {
    return moment(dateString).format('MMM DD, YYYY')
  }

  const chartData = generateChartData()
  const finalCombinedProgress =
    chartData[chartData.length - 1]?.combined_progress || 0

  console.log('🔍 Final render values:', {
    chartDataLength: chartData.length,
    finalCombinedProgress,
    practiceScoresLoading,
    practiceScoresError,
    hasNoPracticeAccess,
    hasChartData,
    hasRealData,
  })

  // Render Central CTA Card
  const renderCentralCTA = () => (
    <CentralCTAOverlay>
      <CentralCTACard>
        <CTAIcon>
          <LockOutlined />
        </CTAIcon>
        <CTATitle>Let's Get Started!</CTATitle>
        <CTADescription>
          Practice to unlock your full Communication progress view.
        </CTADescription>
        <CTAButton type="primary" size="large" onClick={handleContinue}>
          Take Assessment
        </CTAButton>
      </CentralCTACard>
    </CentralCTAOverlay>
  )

  // Main content component
  const MainContent = () => (
    <>
      {/* Progress Visualization Section */}
      <VisualizationSection
        className={practiceScoresLoading ? 'has-overlay' : ''}
      >
        <div className="section-content">
          <Row gutter={24}>
            <Col span={24}>
              <Card
                title="Progress Analytics"
                bordered={false}
                headStyle={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#262626',
                }}
              >
                {practiceScoresError ? (
                  <ErrorContainer>
                    <div className="error-title">
                      Failed to load progress data
                    </div>
                    <div className="error-description">
                      {practiceScoresError}
                    </div>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={handleRetryFetch}
                    >
                      Retry
                    </Button>
                  </ErrorContainer>
                ) : (
                  <ChartContainer
                    className={shouldBlurChart() ? 'demo-mode' : ''}
                    hasOnlyDayZero={hasOnlyDayZeroData()}
                  >
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart
                        data={prepareChartData()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="session"
                          type="number"
                          domain={['dataMin', 'dataMax']}
                          tickCount={Math.min(chartData.length, 10)}
                          stroke="#8c8c8c"
                          fontSize={12}
                          tickLine={false}
                          axisLine={{ stroke: '#d9d9d9', strokeWidth: 1 }}
                          label={{
                            value: 'Days',
                            position: 'insideBottom',
                            offset: -3,
                            style: {
                              textAnchor: 'middle',
                              fill: '#595959',
                              fontSize: 12,
                              fontWeight: 500,
                              fontWeight: 'bold',
                            },
                          }}
                        />
                        <YAxis
                          domain={[0, 100]}
                          stroke="#8c8c8c"
                          fontSize={12}
                          tickLine={false}
                          axisLine={{ stroke: '#d9d9d9', strokeWidth: 1 }}
                          label={{
                            value: 'Scores',
                            angle: -90,
                            position: 'insideLeft',
                            style: {
                              textAnchor: 'middle',
                              fill: '#595959',
                              fontSize: 12,
                              fontWeight: 500,
                              fontWeight: 'bold',
                            },
                          }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                          wrapperStyle={{ paddingTop: '16px', fontSize: 12 }}
                          iconType="line"
                        />
                        <Line
                          type="monotone"
                          dataKey="Reading"
                          stroke="#1890ff"
                          strokeWidth={2}
                          strokeDasharray="4 4"
                          dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#1890ff' }}
                          name="Reading"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Listening"
                          stroke="#fa8c16"
                          strokeWidth={2}
                          strokeDasharray="8 4"
                          dot={{ fill: '#fa8c16', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#fa8c16' }}
                          name="Listening"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Speaking"
                          stroke="#eb2f96"
                          strokeWidth={2}
                          strokeDasharray="6 6"
                          dot={{ fill: '#eb2f96', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#eb2f96' }}
                          name="Speaking"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Writing"
                          stroke="#13c2c2"
                          strokeWidth={2}
                          strokeDasharray="6 6"
                          dot={{ fill: '#13c2c2', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#13c2c2' }}
                          name="Writing"
                          connectNulls={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Combined Progress"
                          stroke="#52c41a"
                          strokeWidth={3}
                          dot={{ fill: '#52c41a', strokeWidth: 3, r: 6 }}
                          activeDot={{ r: 8, fill: '#52c41a' }}
                          name="Combined Progress"
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    {hasOnlyDayZeroData() && communicationDayZero && (
                      <div
                        className="baseline-overlay"
                        style={{ maxWidth: '500px' }}
                      >
                        <div
                          className="title"
                          style={{ fontSize: '16px', marginBottom: '8px' }}
                        >
                          No Progress Data Available
                        </div>
                        <div
                          className="description"
                          style={{ fontSize: '13px', marginBottom: '12px' }}
                        >
                          Start practicing to see the progress here
                        </div>
                        <div
                          className="scores"
                          style={{ fontSize: '13px', gap: '4px' }}
                        >
                          <div
                            style={{ marginBottom: '4px', color: '#8c8c8c' }}
                          >
                            Your scores from diagnosis:
                          </div>
                          <div className="score-item">
                            <span>Reading:</span>
                            <span>{communicationDayZero?.reading || 0}%</span>
                          </div>
                          <div className="score-item">
                            <span>Listening:</span>
                            <span>{communicationDayZero?.listening || 0}%</span>
                          </div>
                          <div className="score-item">
                            <span>Speaking:</span>
                            <span>{communicationDayZero?.speaking || 0}%</span>
                          </div>
                          <div className="score-item">
                            <span>Writing:</span>
                            <span>{communicationDayZero?.writing || 0}%</span>
                          </div>
                          <div className="score-item total">
                            <span>Total:</span>
                            <span>{communicationDayZero.total_score}%</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </ChartContainer>
                )}
              </Card>
            </Col>
          </Row>
        </div>

        {practiceScoresLoading && (
          <SectionOverlay>
            <Spin size="large" className="overlay-spinner" />
            <span className="overlay-text">Loading your progress data...</span>
          </SectionOverlay>
        )}
      </VisualizationSection>

      <TopBar>
        <PageTitle level={4}>Communication Practice</PageTitle>
        <ContinueButton
          onClick={handleContinue}
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Assigning...' : 'Start Practice'}
        </ContinueButton>
      </TopBar>

      {/* Practice History Section */}
      <HistorySection
        className={historyLoading && !hasHistoryData ? 'has-overlay' : ''}
      >
        <div className="section-content">
          <HistoryHeader level={4}>
            Communication Practice History
          </HistoryHeader>
          {historyError ? (
            <ErrorContainer>
              <div className="error-title">Failed to load practice history</div>
              <div className="error-description">{historyError}</div>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRetryFetch}
              >
                Retry
              </Button>
            </ErrorContainer>
          ) : hasHistoryData ? (
            <>
              <SubHeaderText>
                You are doing great! You have practiced communication{' '}
                {practiceHistory.length} time
                {practiceHistory.length !== 1 ? 's' : ''} so far
                {practiceHistoryPagination?.totalCount >
                  practiceHistory.length && (
                  <span style={{ color: '#1890ff', marginLeft: '8px' }}>
                    (Showing {practiceHistory.length} of{' '}
                    {practiceHistoryPagination.totalCount})
                  </span>
                )}
              </SubHeaderText>
              {practiceHistory.map((assessment, index) => {
                const isLatestAssessment = index === 0
                const isRetakeEligible =
                  isLatestAssessment && isRetakeAvailable(assessment)
                return (
                  <OldPracticeItem key={assessment.id || index}>
                    <OldPracticeContent>
                      <Col flex="1" style={{ minWidth: 0 }}>
                        <PracticeItemLeft>
                          <BadgeContainer>
                            <DateBadge>
                              {formatAssessmentDate(assessment.submittedAt)}
                            </DateBadge>
                            {isLatestAssessment && (
                              <LatestBadge>Latest</LatestBadge>
                            )}
                          </BadgeContainer>
                          <OldPracticeTitle>
                            {assessment.title}
                          </OldPracticeTitle>
                        </PracticeItemLeft>
                      </Col>
                      <Col flex="none">
                        <ScoresContainer>
                          <ScoreItem>
                            <div className="label">Duration</div>
                            <div className="value">
                              {assessment.duration?.toFixed(1) || 'N/A'} min
                            </div>
                          </ScoreItem>
                          <ScoreItem>
                            <div className="label">Status</div>
                            <div
                              className="value"
                              style={{
                                color: assessment.scoresCalculated
                                  ? '#52c41a'
                                  : '#fa8c16',
                              }}
                            >
                              {assessment.scoresCalculated
                                ? 'Completed'
                                : 'Pending'}
                            </div>
                          </ScoreItem>
                          <ActionButtons>
                            <ViewScoreButton
                              type="primary"
                              size="small"
                              disabled={!assessment.scoresCalculated}
                              loading={reportsLoading}
                              onClick={() => handleViewScore(assessment)}
                            >
                              {reportsLoading ? 'Loading...' : 'View Score'}
                            </ViewScoreButton>
                            {/* Retake button - only for latest assessment */}
                            {isLatestAssessment &&
                              assessment.scoresCalculated && (
                                <Tooltip
                                  title={
                                    retakeEligibilityLoading
                                      ? 'Checking eligibility...'
                                      : isRetakeEligible
                                      ? 'Retake this communication assessment'
                                      : retakeEligibility?.reason ||
                                        'Retake not available'
                                  }
                                  placement="top"
                                >
                                  <RetakeButton
                                    size="small"
                                    disabled={
                                      !isRetakeEligible ||
                                      retakeLoading ||
                                      retakeEligibilityLoading
                                    }
                                    loading={retakeLoading}
                                    onClick={() =>
                                      handleRetakeAssessment(assessment)
                                    }
                                  >
                                    {retakeLoading ? 'Starting...' : 'Retake'}
                                  </RetakeButton>
                                </Tooltip>
                              )}
                          </ActionButtons>
                        </ScoresContainer>
                      </Col>
                    </OldPracticeContent>
                  </OldPracticeItem>
                )
              })}
              {/* View More Button */}
              {practiceHistoryPagination?.hasNextPage && (
                <ViewMoreButton
                  onClick={handleLoadMore}
                  loading={loadingMore}
                  disabled={loadingMore}
                >
                  {loadingMore
                    ? 'Loading more...'
                    : `View More (${
                        practiceHistoryPagination.totalCount -
                        practiceHistory.length
                      } remaining)`}
                </ViewMoreButton>
              )}
              {/* Loading indicator for additional items */}
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Spin size="small" />
                  <Text style={{ marginLeft: 8, color: '#8c8c8c' }}>
                    Loading more communication assessments...
                  </Text>
                </div>
              )}
            </>
          ) : (
            <EmptyContainer>
              <div className="empty-title">
                No communication practice assessments completed yet
              </div>
              <div className="empty-description">
                Complete your first communication practice assessment to see
                your progress here!
              </div>
            </EmptyContainer>
          )}
        </div>

        {historyLoading && !hasHistoryData && (
          <SectionOverlay>
            <Spin size="large" className="overlay-spinner" />
            <span className="overlay-text">
              Loading your practice history...
            </span>
          </SectionOverlay>
        )}
      </HistorySection>

      {/* Score Drawer */}
      {scoreDrawerVisible && (
        <ScoreDrawer
          visible={scoreDrawerVisible}
          onClose={handleCloseScoreDrawer}
          assessment={selectedAssessment}
          reports={reports}
          loading={reportsLoading}
          error={reportsError}
          onDownloadReport={handleDownloadReport}
          onViewDetailedReport={handleViewDetailedReport}
        />
      )}
    </>
  )

  return (
    <StyledCommunicationContainer>
      {hasNoPracticeAccess ? (
        <>
          <BlurWrapper>
            <MainContent />
          </BlurWrapper>
          {/* {renderCentralCTA()} */}
        </>
      ) : (
        <MainContent />
      )}
    </StyledCommunicationContainer>
  )
}

export default Communication
